"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const partners = [{
  name: "Microsoft",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580361/img/logo_microsoft.png"
}, {
  name: "SAP",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580361/img/logo_sap.png"
}, {
  name: "Oracle",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580361/img/logo_oracle.png"
}, {
  name: "Salesforce",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580361/img/logo_salesforce.png"
}, {
  name: "AWS",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580361/img/logo_aws.png"
}, {
  name: "IBM",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580361/img/logo_ibm.png"
}, {
  name: "Cisco",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580361/img/logo_cisco.png"
}, {
  name: "Workday",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580361/img/logo_workday.png"
}];
export default function EcosystemIntegrations() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="ecosystem-integrations" className="bg-background py-24 sm:py-32">
      <div ref={ref} className={`mx-auto max-w-7xl px-6 lg:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl font-sans">
            Seamless Interoperability
          </h2>
          <p className="mt-6 text-lg leading-8 text-muted-foreground font-sans">
            Kanha ERP integrates flawlessly with existing federal and state systems, ensuring secure data exchange, regulatory compliance, and operational continuity across all departments.
          </p>
        </div>
        
        <div className="mx-auto mt-16 grid max-w-lg grid-cols-2 items-center gap-x-8 gap-y-12 sm:max-w-xl sm:grid-cols-4 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-4">
          {partners.map((partner, i) => <div data-index={i} key={partner.name} className="col-span-1 flex justify-center">
              <img className="max-h-12 w-full object-contain grayscale opacity-50 transition-all duration-300 hover:grayscale-0 hover:opacity-100" src={partner.logo} alt={`${partner.name} logo`} width={158} height={48} />
            </div>)}
        </div>
      </div>
    </section>;
}